import React, { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
} from "@mui/material";
import { ArrowBack, LocationOn } from "@mui/icons-material";
import { Scaffold } from "../Scaffold";
import LoadingSpinner from "../LoadingSpinner";
import { State } from "../redux/types";
import { useSelector, useDispatch } from "react-redux";
import { maskPhone, dateFormatter } from "../utils/masking";
import { ConfirmActionDialog } from "../components/ConfirmActionDialog";
import { VendorUpdateDialog } from "../components/VendorUpdateDialog";
import { useVendorActions } from "../hooks/useVendorActions";
import { vendorRequest } from "../api/api";
import { setAdvanceResult } from "../redux/actions";

type Address = {
  id?: string;
  street?: string;
  street2?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  postalCode?: string;
  country?: string;
  isDefault?: boolean;
  favorite?: boolean;
  [key: string]: unknown;
};

const VENDOR_KEY = "cognito";

export const Cognito = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const advanceVendorResult = useSelector(
    (state: State) => state.advanceResult
  );

  // Get customerVendorId from navigation state (passed from AdvanceResult)
  const locationState = location.state as { customerVendorId?: string } | null;

  // Try multiple possible property names for the Cognito ID, with navigation state as fallback
  const cognitoId = advanceVendorResult?.customer?.cognitoId 
    ?? advanceVendorResult?.customer?.sub 
    ?? advanceVendorResult?.customer?.customerVendorId 
    ?? advanceVendorResult?.customer?.id
    ?? advanceVendorResult?.value?.cognitoId
    ?? advanceVendorResult?.value?.sub
    ?? locationState?.customerVendorId
    ?? "";

  // Address modal state
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Get addresses array from customer data (vendor endpoints may use different field names)
  const addresses: Address[] = advanceVendorResult?.customer?.userAddressBooksList
    ?? advanceVendorResult?.customer?.userAddressBooks
    ?? advanceVendorResult?.customer?.userDeliveryAddressList
    ?? [];
  const hasMultipleAddresses = Array.isArray(addresses) && addresses.length > 1;

  const formatAddress = (address: Address | string): string => {
    if (typeof address === "string") {
      return address;
    }
    const parts = [
      address.street ?? address.addressLine1,
      address.street2 ?? address.addressLine2,
      address.city,
      address.state,
      address.zipCode ?? address.postalCode,
      address.country,
    ].filter(Boolean);
    return parts.join(", ") || "N/A";
  };

  // Get display text for address field
  const getAddressDisplayText = (): string => {
    if (!addresses || addresses.length === 0) {
      return "N/A";
    }
    if (Array.isArray(addresses)) {
      if (addresses.length === 1) {
        return formatAddress(addresses[0]);
      }
      return `${addresses.length} addresses on file`;
    }
    return String(addresses);
  };

  // Handle address click
  const handleAddressClick = () => {
    if (hasMultipleAddresses) {
      setAddressModalOpen(true);
    }
  };

  const handleBackToResults = () => {
    navigate("/advanceresult");
  };

  // Refresh vendor data after successful action
  const handleVendorActionSuccess = useCallback(async (actionKey: string) => {
    if (actionKey === "delete") {
      navigate("/advanceresult");
      return;
    }

    if (!cognitoId) { return; }

    setIsRefreshing(true);
    try {
      await new Promise<void>(resolve => { setTimeout(resolve, 1000); });
      const response = await vendorRequest(cognitoId, VENDOR_KEY);
      if (response) {
        dispatch(setAdvanceResult({ ...response }));
      }
    } catch (error) {
      console.debug("Cognito vendor refetch failed", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [cognitoId, dispatch, navigate]);

  // ============================================
  // Vendor Actions (UPDATE & DELETE)
  // ============================================

  const {
    dialogOpen: vendorDialogOpen,
    updateDialogOpen: vendorUpdateDialogOpen,
    currentAction: vendorCurrentAction,
    loading: vendorLoading,
    openDialog: openVendorDialog,
    closeDialog: closeVendorDialog,
    closeUpdateDialog: closeVendorUpdateDialog,
    confirmAction: confirmVendorAction,
    confirmUpdate: confirmVendorUpdate,
  } = useVendorActions({
    vendorKey: VENDOR_KEY,
    customerVendorId: cognitoId,
    vendorName: "Cognito",
    onSuccess: handleVendorActionSuccess,
    onFailure: () => {
      // Handle failure if needed
    },
  });

  const buttonStyle = {
    backgroundColor: "#041E42",
    color: "white",
    px: 3,
    py: 1,
    textTransform: "none" as const,
    "&:hover": {
      backgroundColor: "#0a3d7a",
    },
  };

  if (!advanceVendorResult) {
    return (
      <Scaffold navBar center>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <LoadingSpinner />
        </Box>
      </Scaffold>
    );
  }

  return (
    <Scaffold navBar center>
      {isRefreshing && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            bgcolor: "rgba(255,255,255,0.7)",
          }}
        >
          <LoadingSpinner />
        </Box>
      )}
      {/* Back Button - Fixed to top left */}
      <Box
        sx={{
          position: "fixed",
          top: 80,
          left: 20,
          zIndex: 10,
          mt: "2rem",
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackToResults}
          sx={{
            color: "text.secondary",
            textTransform: "none",
            fontSize: "0.875rem",
          }}
        >
          BACK TO RESULTS
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Cognito Title - Centered */}
        <Typography
          variant="h4"
          sx={{ mb: 3, fontWeight: "bold", textAlign: "center", width: "100%" }}
        >
          Cognito
        </Typography>

        {/* Customer Account Card */}
        <Card
            sx={{
              border: "3px solid rgba(0,0,0,0.1)",
              borderRadius: 2,
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              minWidth: "400px",
              maxWidth: "600px"
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                Customer Account
              </Typography>

              {advanceVendorResult ? (
                <Stack spacing={1.5}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Name:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {advanceVendorResult.customer?.firstName}{" "}
                      {advanceVendorResult.customer?.lastName}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Email:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {advanceVendorResult.customer?.email}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Phone:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {maskPhone(advanceVendorResult.customer?.mobilePhoneNumber ?? "")}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Signup Date:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {(advanceVendorResult.customer?.signUpDate ?? advanceVendorResult.customer?.signupDate)
                        ? dateFormatter(advanceVendorResult.customer?.signUpDate ?? advanceVendorResult.customer?.signupDate)
                        : "N/A"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Cognito Confirmed:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {advanceVendorResult.customer?.cognitoConfirmationStatus
                        ?? advanceVendorResult.customer?.confirmationStatus
                        ?? "N/A"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Cognito Enabled:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "right",
                        fontWeight: "bold",
                        color: advanceVendorResult.customer?.cognitoEnabled === "true" || advanceVendorResult.customer?.cognitoEnabled === true
                          ? "success.main"
                          : "error.main",
                      }}
                    >
                      {advanceVendorResult.customer?.cognitoEnabled === "true" || advanceVendorResult.customer?.cognitoEnabled === true
                        ? "Enabled"
                        : "Disabled"}
                    </Typography>
                  </Box>

                  {/* Address field - clickable if multiple addresses */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Address:
                    </Typography>
                    {hasMultipleAddresses ? (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleAddressClick}
                        sx={{
                          textTransform: "none",
                          color: "#041E42",
                          fontWeight: "bold",
                          textDecoration: "underline",
                          p: 0,
                          minWidth: "auto",
                          "&:hover": {
                            backgroundColor: "transparent",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {getAddressDisplayText()}
                      </Button>
                    ) : (
                      <Typography variant="body2" sx={{ textAlign: "right" }}>
                        {getAddressDisplayText()}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No customer data available
                </Typography>
              )}

              {/* Action Buttons */}
              <Box
                sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}
              >
                <Button
                  variant="contained"
                  sx={buttonStyle}
                  onClick={() => openVendorDialog("update")}
                >
                  UPDATE
                </Button>

                <Button
                  variant="contained"
                  sx={{ ...buttonStyle, backgroundColor: "#b71c1c", "&:hover": { backgroundColor: "#7f0000" } }}
                  onClick={() => openVendorDialog("delete")}
                >
                  DELETE
                </Button>

              </Box>
            </CardContent>
          </Card>
      </Box>

      {/* Address List Modal */}
      <Dialog
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocationOn color="primary" />
          Saved Addresses ({addresses.length})
        </DialogTitle>
        <DialogContent dividers>
          <List disablePadding>
            {addresses.map((address, index) => (
              <React.Fragment key={address.id ?? index}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    py: 2,
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Address {index + 1}
                    </Typography>
                    {(address.isDefault || address.favorite) && (
                      <Chip label={address.isDefault ? "Default" : "Favorite"} size="small" color="primary" />
                    )}
                  </Box>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        {address.street ?? address.addressLine1 ?? "No street address"}
                        {(address.street2 ?? address.addressLine2) && `, ${address.street2 ?? address.addressLine2}`}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {[address.city, address.state, address.zipCode ?? address.postalCode, address.country]
                          .filter(Boolean)
                          .join(", ")}
                      </Typography>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressModalOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Vendor Actions (DELETE) */}
      <ConfirmActionDialog
        open={vendorDialogOpen}
        actionLabel={vendorCurrentAction?.label ?? ""}
        description={vendorCurrentAction?.description ?? ""}
        confirmText={vendorCurrentAction?.confirmText}
        loading={vendorLoading}
        onConfirm={confirmVendorAction}
        onCancel={closeVendorDialog}
      />

      {/* Vendor Update Dialog with Form */}
      <VendorUpdateDialog
        open={vendorUpdateDialogOpen}
        vendorName="Cognito"
        loading={vendorLoading}
        initialValues={{
          firstName: advanceVendorResult?.customer?.firstName,
          lastName: advanceVendorResult?.customer?.lastName,
          email: advanceVendorResult?.customer?.email,
          phoneNumber: advanceVendorResult?.customer?.mobilePhoneNumber,
        }}
        onConfirm={confirmVendorUpdate}
        onCancel={closeVendorUpdateDialog}
        hideBirthDate
      />
    </Scaffold>
  );
};
